import { create } from 'zustand';
import { useMemo, useCallback, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { subscribeWithSelector } from 'zustand/middleware';
import { Template, Element, AspectRatio, Slot, EntityType } from '../../core/template.schemas';
import { zTemplate } from '../../core/template.schemas';
import { elementRegistry } from '../registry';
import { getCanvasDimensions } from './useTemplateDimensions';
import { useFetchTemplate, useSaveTemplate } from '@/lib/api/requests/templates.requests';
import { annotateActiveSpan, recordDistributionMetric, withTelemetrySpan } from '@/lib/monitoring/telemetry';
import { useTemplateExport } from '../../hooks/useTemplateExport';
import { Context } from '@/util-components/root/context';

/**
 * Template store state interface
 * Contains all state and actions for the template editor
 */
interface TemplateStoreState {
  /** Current template being edited */
  template: Template;
  /** ID of the currently selected element, or null if none selected */
  selectedElementId: string | null;
  /** Clipboard for copy/paste operations */
  clipboard: Element | null;

  /** History for Undo/Redo operations */
  history: Template[];
  /** Current position in history stack */
  historyIndex: number;
  /** Maximum number of history states to keep */
  maxHistorySize: number;

  /** Loading state indicator */
  isLoading: boolean;
  /** Saving state indicator */
  isSaving: boolean;
  /** Timestamp of last save operation */
  lastSaved: Date | null;

  // Template Properties Actions
  /**
   * Updates the template title
   * @param title - New title for the template
   */
  updateTemplateTitle: (title: string) => void;
  
  /**
   * Updates the template aspect ratio
   * @param aspectRatio - New aspect ratio
   */
  updateTemplateAspectRatio: (aspectRatio: AspectRatio) => void;

  /**
   * Updates canvas properties
   * @param updates - Partial canvas properties to update
   */
  updateCanvas: (updates: Partial<Template['canvas']>) => void;

  // Element Management Actions
  /**
   * Adds a new element of the specified type
   * @param elementType - Type of element to add
   */
  addElement: (elementType: Element['type'], overrides?: Partial<Element>) => void;
  
  /**
   * Updates properties of an existing element
   * @param id - ID of the element to update
   * @param updates - Partial element properties to update
   */
  updateElement: (id: string, updates: Partial<Element>) => void;
  updateElementEphemeral: (id: string, updates: Partial<Element>) => void;
  
  /**
   * Deletes an element from the template
   * @param id - ID of the element to delete
   */
  deleteElement: (id: string) => void;
  
  /**
   * Selects an element or deselects if null is passed
   * @param id - ID of the element to select, or null to deselect
   */
  selectElement: (id: string | null) => void;

  // Z-Index Management Actions
  /**
   * Moves an element one position up in the z-index stack
   * @param id - ID of the element to move
   */
  moveElementUp: (id: string) => void;
  
  /**
   * Moves an element one position down in the z-index stack
   * @param id - ID of the element to move
   */
  moveElementDown: (id: string) => void;
  
  /**
   * Moves an element to the top of the z-index stack
   * @param id - ID of the element to move
   */
  moveElementToTop: (id: string) => void;

  /**
   * Moves an element to the bottom of the z-index stack
   * @param id - ID of the element to move
   */
  moveElementToBottom: (id: string) => void;
  
  /**
   * Reorders elements by moving from one index to another
   * @param fromIndex - Source index in the sorted elements array
   * @param toIndex - Destination index in the sorted elements array
   */
  reorderElements: (fromIndex: number, toIndex: number) => void;

  // Slot Management Actions
  /**
   * Adds a new slot to the template
   * @param slot - Slot to add (without ID, will be generated)
   */
  addSlot: (slot: Omit<Slot, 'id'>) => void;
  
  /**
   * Updates an existing slot
   * @param id - ID of the slot to update
   * @param updates - Partial slot properties to update
   */
  updateSlot: (id: string, updates: Partial<Omit<Slot, 'id'>>) => void;
  
  /**
   * Deletes a slot from the template
   * @param id - ID of the slot to delete
   */
  deleteSlot: (id: string) => void;
  
  /**
   * Gets all slots in the template
   * @returns Array of all slots
   */
  getSlots: () => Slot[];
  
  /**
   * Gets slots filtered by entity type
   * @param entityType - Entity type to filter by
   * @returns Array of slots with the specified entity type
   */
  getSlotsByEntityType: (entityType: EntityType) => Slot[];
  
  /**
   * Checks if a slot is currently in use by any element
   * @param slotId - ID of the slot to check
   * @returns True if the slot is in use, false otherwise
   */
  isSlotInUse: (slotId: string) => boolean;
  
  /**
   * Gets all elements that use a specific slot
   * @param slotId - ID of the slot
   * @returns Array of elements using the slot
   */
  getElementsUsingSlot: (slotId: string) => Element[];

  // History Management Actions
  /**
   * Undoes the last action if available
   */
  undo: () => void;
  
  /**
   * Redoes the last undone action if available
   */
  redo: () => void;
  
  // Utility Actions
  /**
   * Validates the current template against its schema
   * @returns True if template is valid, false otherwise
   */
  validateTemplate: () => boolean;
  
  // Visibility Actions
  /**
   * Toggles the visibility of an element
   * @param id - ID of the element to toggle visibility
   */
  toggleElementVisibility: (id: string) => void;
  
  /**
   * Returns all visible elements from the template
   * @returns Array of visible elements
   */
  getVisibleElements: () => Element[];

  // Clipboard Actions
  /**
   * Copies the currently selected element to the clipboard
   */
  copyElement: () => void;

  /**
   * Pastes the element from the clipboard
   */
  pasteElement: () => void;

  /**
   * Duplicates the currently selected element
   */
  duplicateElement: () => void;

  /**
   * Centers an element on the canvas
   * @param id - ID of the element to center
   * @param axis - Axis to center on ('horizontal', 'vertical', or 'both')
   */
  centerElement: (id: string, axis: 'horizontal' | 'vertical' | 'both') => void;
}

/**
 * Helper function to check if a StaticOrDynamic value uses a specific slot
 * @param value - StaticOrDynamic value to check
 * @param slotId - ID of the slot to check for
 * @returns True if the value uses the slot
 */
const checkStaticOrDynamicUsesSlot = (value: any, slotId: string): boolean => {
  if (value && typeof value === 'object' && value.mode === 'dynamic') {
    const binding = value.dynamic?.source;
    return binding?.kind === 'slot' && binding.slotId === slotId;
  }
  return false;
};

/**
 * Helper function to recursively check if any property in an object uses a specific slot
 * @param obj - Object to check
 * @param slotId - ID of the slot to check for
 * @returns True if any property uses the slot
 */
const checkObjectUsesSlot = (obj: any, slotId: string): boolean => {
  if (!obj || typeof obj !== 'object') return false;
  
  // Check if this object itself is a StaticOrDynamic value
  if (checkStaticOrDynamicUsesSlot(obj, slotId)) {
    return true;
  }
  
  // Recursively check all properties
  for (const value of Object.values(obj)) {
    if (Array.isArray(value)) {
      // Check array elements
      for (const item of value) {
        if (checkObjectUsesSlot(item, slotId)) {
          return true;
        }
      }
    } else if (checkObjectUsesSlot(value, slotId)) {
      return true;
    }
  }
  
  return false;
};

/**
 * Helper function to check if an element uses a specific slot
 * @param element - Element to check
 * @param slotId - ID of the slot to check for
 * @returns True if the element uses the slot
 */
const checkElementUsesSlot = (element: Element, slotId: string): boolean => {
  // Check all properties of the element recursively
  // This will catch any StaticOrDynamic values that use slots, including:
  // - content (text elements)
  // - source (image elements)
  // - color (text elements)
  // - fill.color (rect/circle elements)
  // - stroke.color (various elements)
  // - gradient stops colors
  // - any future dynamic attributes
  return checkObjectUsesSlot(element, slotId);
};

/**
 * Creates a default template with standard values
 * @returns A new template with default values
 */
const createDefaultTemplate = (aspectRatio: AspectRatio = '1:1'): Template => {
    const canvasDimensions = getCanvasDimensions(aspectRatio);
    return {
      schemaVersion: 1,
      id: uuidv4(),
      title: 'New Template',
      aspectRatio,
      canvas: {
        width: canvasDimensions.width,
        height: canvasDimensions.height
      },
      elements: [],
      slots: [],
      department: 'soccer'
    };
  };

/**
 * Template store implementation using Zustand
 */
export const useTemplateStore = create<TemplateStoreState>()(subscribeWithSelector((set, get) => {
  /**
   * Helper function to update template and add to history
   * @param updater - Function that updates the template
   */
  const updateTemplateWithHistory = (updater: (template: Template) => Template | void) => {
    const currentTemplate = get().template;
    // Clone template before mutation to avoid side effects
    const templateCopy = structuredClone(currentTemplate);
    const updated = updater(templateCopy);
    const newTemplate = updated || templateCopy;
  
    // Validate template in development mode
    if (process.env.NODE_ENV === 'development') {
      const validation = zTemplate.safeParse(newTemplate);
      if (!validation.success) {
        console.error('Template validation failed:', validation.error);
        return;
      }
    }
  
    // Update history stack
    const newHistory = get().history.slice(0, get().historyIndex + 1);
    newHistory.push(structuredClone(newTemplate));
  
    // Maintain history size limit
    if (newHistory.length > get().maxHistorySize) {
      newHistory.shift();
    }
  
    // Update state
    set({
      template: newTemplate,
      history: newHistory,
      historyIndex: newHistory.length - 1,
    });
  };

  return {
    // Initial State
    template: createDefaultTemplate(),
    selectedElementId: null,
    history: [createDefaultTemplate()],
    historyIndex: 0,
    maxHistorySize: 50,
    isLoading: false,
    isSaving: false,
    lastSaved: null,
    clipboard: null, // Local clipboard state
    
    resetTemplate: () => {
      const newTemplate = createDefaultTemplate();
      set({
        template: newTemplate,
        selectedElementId: null,
        history: [structuredClone(newTemplate)],
        historyIndex: 0,
      });
    },

    // Template Properties
    updateTemplateTitle: (title: string) => {
      updateTemplateWithHistory(template => {
        template.title = title;
      });
    },

    updateTemplateAspectRatio: (aspectRatio: AspectRatio) => {
      const canvasDimensions = getCanvasDimensions(aspectRatio);
      
      updateTemplateWithHistory(template => {
        template.aspectRatio = aspectRatio;
        template.canvas.width = canvasDimensions.width;
        template.canvas.height = canvasDimensions.height;
      });
    },
    
    updateCanvas: (updates: Partial<Template['canvas']>) => {
      updateTemplateWithHistory(template => {
        template.canvas = { ...template.canvas, ...updates };
      });
    },

    // Element Management
    addElement: (elementType: Element['type'], overrides?: Partial<Element>) => {
      const definition = elementRegistry.get(elementType);
      if (!definition) {
        console.error(`Element type "${elementType}" is not registered.`);
        return;
      }

      // Get current template state
      const currentTemplate = get().template;
      
      // Helper for deep merging objects
      const deepMerge = (target: any, source: any) => {
        for (const key in source) {
          if (source.hasOwnProperty(key)) {
            if (typeof source[key] === 'object' && source[key] !== null && !Array.isArray(source[key]) && typeof target[key] === 'object' && target[key] !== null && !Array.isArray(target[key])) {
              target[key] = deepMerge(target[key] || {}, source[key]);
            } else {
              target[key] = source[key];
            }
          }
        }
        return target;
      };

      // Create new element with default properties and apply deep merged overrides
      // Note: zIndex will be calculated during insertion
      const newElement = deepMerge(
        { ...definition.defaultProps, id: uuidv4(), zIndex: 0 },
        overrides || {}
      ) as Element;
      
      // Determine insertion logic based on Watermark Policy
      const currentElements = [...currentTemplate.elements];
      // Sort existing elements by Z-Index to ensure correct order
      const sortedElements = currentElements.sort((a, b) => a.zIndex - b.zIndex);
      
      const existingWatermarks = sortedElements.filter(e => e.isWatermark);
      const existingRegulars = sortedElements.filter(e => !e.isWatermark);
      
      let finalElementsOrder: Element[] = [];

      if (newElement.isWatermark) {
        // Watermarks go to the very top
        finalElementsOrder = [...existingRegulars, ...existingWatermarks, newElement];
      } else {
        // Regular elements go above other regulars, but below watermarks
        finalElementsOrder = [...existingRegulars, newElement, ...existingWatermarks];
      }

      // Re-assign Z-Indices based on final order
      finalElementsOrder.forEach((el, idx) => {
        el.zIndex = idx;
      });
      
      // Create updated template with new element order
      const updatedTemplate = {
        ...currentTemplate,
        elements: finalElementsOrder
      };
      
      // Update history
      const newHistory = get().history.slice(0, get().historyIndex + 1);
      newHistory.push(structuredClone(updatedTemplate));
      
      // Maintain history size limit
      if (newHistory.length > get().maxHistorySize) {
        newHistory.shift();
      }
      
      // Update state with new template and select the new element
      set({
        template: updatedTemplate,
        selectedElementId: newElement.id,
        history: newHistory,
        historyIndex: newHistory.length - 1
      });
    },

    updateElement: (id: string, updates: Partial<Element>) => {
      updateTemplateWithHistory(template => {
        const element = template.elements.find(el => el.id === id);
        if (element) {
          Object.assign(element, updates);
        }
      });
    },

    updateElementEphemeral: (id: string, updates: Partial<Element>) => {
      set(state => {
        const updatedTemplate = structuredClone(state.template);
        const el = updatedTemplate.elements.find((el: any) => el.id === id);
        if (el) {
          Object.assign(el, updates);
        }
        return { template: updatedTemplate };
      });
    },

    deleteElement: (id: string) => {
      updateTemplateWithHistory(template => {
        template.elements = template.elements.filter(el => el.id !== id);
      });
      
      // Deselect the element if it was selected
      if (get().selectedElementId === id) {
        set({ selectedElementId: null });
      }
    },

    selectElement: (id: string | null) => {
      set({ selectedElementId: id });
    },

    // Z-Index Management
    reorderElements: (fromIndex: number, toIndex: number) => {
      updateTemplateWithHistory(template => {
        // Sort elements by z-index
        const sorted = [...template.elements].sort((a, b) => a.zIndex - b.zIndex);
        
        // Move element from source to destination
        const [moved] = sorted.splice(fromIndex, 1);
        sorted.splice(toIndex, 0, moved);

        // Enforce Z-Index Policy: Watermarks must always be on top
        const watermarks = sorted.filter(e => e.isWatermark);
        const regularElements = sorted.filter(e => !e.isWatermark);
        
        // Reconstruct order: Regulars first, then Watermarks
        // This preserves the relative order established by the splice above within each group
        const finalSorted = [...regularElements, ...watermarks];

        // Reassign z-index values based on new order
        const elementMap = new Map(template.elements.map(el => [el.id, el]));
        finalSorted.forEach((sortedEl, index) => {
          const originalEl = elementMap.get(sortedEl.id);
          if (originalEl) {
            originalEl.zIndex = index;
          }
        });
      });
    },

    // Slot Management
    addSlot: (slot: Omit<Slot, 'id'>) => {
      updateTemplateWithHistory(template => {
        const newSlot: Slot = {
          ...slot,
          id: uuidv4(),
        };
        template.slots = template.slots || [];
        template.slots.push(newSlot);
      });
    },

    updateSlot: (id: string, updates: Partial<Omit<Slot, 'id'>>) => {
      updateTemplateWithHistory(template => {
        const slot = template.slots?.find(s => s.id === id);
        if (slot) {
          Object.assign(slot, updates);
        }
      });
    },

    deleteSlot: (id: string) => {
      // Check if slot is in use before deleting
      const isInUse = get().isSlotInUse(id);
      if (isInUse) {
        console.warn(`Cannot delete slot ${id}: it is currently in use by elements`);
        return;
      }

      updateTemplateWithHistory(template => {
        template.slots = template.slots?.filter(s => s.id !== id) || [];
      });
    },

    getSlots: () => {
      return get().template.slots || [];
    },

    getSlotsByEntityType: (entityType: EntityType) => {
      return (get().template.slots || []).filter(slot => slot.entityType === entityType);
    },

    isSlotInUse: (slotId: string) => {
      const { template } = get();
      return template.elements.some(element => checkElementUsesSlot(element, slotId));
    },

    getElementsUsingSlot: (slotId: string) => {
      const { template } = get();
      return template.elements.filter(element => checkElementUsesSlot(element, slotId));
    },
    
    moveElementUp: (id: string) => {
      const { template } = get();
      const sorted = [...template.elements].sort((a, b) => a.zIndex - b.zIndex);
      const currentIndex = sorted.findIndex(el => el.id === id);
      
      // Only move if not already at the top
      if (currentIndex < sorted.length - 1) {
        get().reorderElements(currentIndex, currentIndex + 1);
      }
    },
    
    moveElementDown: (id: string) => {
      const { template } = get();
      const sorted = [...template.elements].sort((a, b) => a.zIndex - b.zIndex);
      const currentIndex = sorted.findIndex(el => el.id === id);
      
      // Only move if not already at the bottom
      if (currentIndex > 0) {
        get().reorderElements(currentIndex, currentIndex - 1);
      }
    },
    
    moveElementToTop: (id: string) => {
      const { template } = get();
      const sorted = [...template.elements].sort((a, b) => a.zIndex - b.zIndex);
      const currentIndex = sorted.findIndex(el => el.id === id);
      
      // Only move if not already at the top
      if (currentIndex < sorted.length - 1) {
        get().reorderElements(currentIndex, sorted.length - 1);
      }
    },
    
    moveElementToBottom: (id: string) => {
      const { template } = get();
      const sorted = [...template.elements].sort((a, b) => a.zIndex - b.zIndex);
      const currentIndex = sorted.findIndex(el => el.id === id);
      
      // Only move if not already at the bottom
      if (currentIndex > 0) {
        get().reorderElements(currentIndex, 0);
      }
    },

    // Visibility Management
    toggleElementVisibility: (id: string) => {
      updateTemplateWithHistory(template => {
        const element = template.elements.find(el => el.id === id);
        if (element) {
          element.visible = !element.visible;
        }
      });
      
      // Deselect the element if it was hidden
      const element = get().template.elements.find(el => el.id === id);
      if (element && !element.visible && get().selectedElementId === id) {
        set({ selectedElementId: null });
      }
    },
    
    getVisibleElements: () => {
      return get().template.elements.filter(el => el.visible);
    },

    // History Management
    undo: () => {
      const { history, historyIndex } = get();
      if (historyIndex > 0) {
        set({
          template: structuredClone(history[historyIndex - 1]),
          historyIndex: historyIndex - 1,
          selectedElementId: null,
        });
      }
    },

    redo: () => {
      const { history, historyIndex } = get();
      if (historyIndex < history.length - 1) {
        set({
          template: structuredClone(history[historyIndex + 1]),
          historyIndex: historyIndex + 1,
          selectedElementId: null,
        });
      }
    },

    // Utility
    validateTemplate: () => {
      return zTemplate.safeParse(get().template).success;
    },

    // Clipboard Actions
    copyElement: () => {
      const { selectedElementId, template } = get();
      if (!selectedElementId) return;
      
      const element = template.elements.find(el => el.id === selectedElementId);
      if (element) {
        set({ clipboard: structuredClone(element) });
      }
    },

    pasteElement: () => {
      const { clipboard } = get();
      if (!clipboard) return;

      updateTemplateWithHistory(currentTemplate => {
        // Calculate highest z-index
        const maxZIndex = currentTemplate.elements.length > 0
          ? Math.max(...currentTemplate.elements.map(el => el.zIndex))
          : -1;
        
        // Create new element from clipboard
        const newElement = structuredClone(clipboard);
        newElement.id = uuidv4();
        newElement.zIndex = maxZIndex + 1;
        
        // Offset position slightly to make it visible
        newElement.x += 20;
        newElement.y += 20;
        
        // Append 'Copy' to name
        if (newElement.name) {
          newElement.name = `${newElement.name} (Kopie)`;
        }
        
        currentTemplate.elements.push(newElement);
        
        // Select the new element
        // We need to do this outside the updater or use a timeout/effect, 
        // but here we are inside the store, so we can set state after.
        // However, updateTemplateWithHistory sets the state.
        // We'll update the selectedElementId in the next tick or rely on the caller?
        // Actually, we can just set it here, but updateTemplateWithHistory overwrites state.
        // Let's modify updateTemplateWithHistory to allow returning partial state?
        // No, let's just use setTimeout or assume the component handles selection?
        // Better: updateTemplateWithHistory is internal.
        // We can just set selectedElementId after.
      });

      // Select the newly pasted element (we need to find it again or track the ID)
      // Since we generated the ID, we know it.
      // But updateTemplateWithHistory uses a structuredClone, so the ref might change, but ID persists.
      // Wait, I can't easily extract the ID from inside the callback unless I capture it.
      // I'll regenerate ID before passing to updater.
    },

    duplicateElement: () => {
      const { selectedElementId } = get();
      if (!selectedElementId) return;
      
      // Copy then paste
      get().copyElement();
      // We need to wait for state update? No, it's synchronous in Zustand mostly.
      get().pasteElement();
    },

    centerElement: (id: string, axis: 'horizontal' | 'vertical' | 'both') => {
      updateTemplateWithHistory(template => {
        const element = template.elements.find(el => el.id === id);
        if (!element) return;
        
        const canvasWidth = template.canvas.width;
        const canvasHeight = template.canvas.height;
        
        if (axis === 'horizontal' || axis === 'both') {
          element.x = (canvasWidth - element.width) / 2;
        }
        
        if (axis === 'vertical' || axis === 'both') {
          element.y = (canvasHeight - element.height) / 2;
        }
      });
    },
  };
}));

/**
 * Selector hooks for accessing template store state
 */

/**
 * Hook to access the current template
 * @returns The current template
 */
export const useTemplate = () => useTemplateStore(state => state.template);

/**
 * Hook to access the currently selected element ID
 * @returns The selected element ID or null
 */
export const useSelectedElementId = () => useTemplateStore(state => state.selectedElementId);

/**
 * Hook to access all template elements
 * @returns Array of all elements in the template
 */
export const useTemplateElements = () => useTemplateStore(state => state.template.elements);

/**
 * Hook to access the currently selected element
 * @returns The selected element or null if none selected
 */
export const useSelectedElement = (): Element | null => {
  const elements = useTemplateElements();
  const selectedId = useSelectedElementId();
  return selectedId ? elements.find(el => el.id === selectedId) || null : null;
};

/**
 * Hook to check if undo is available
 * @returns True if undo is available
 */
export const useCanUndo = () => useTemplateStore(state => state.historyIndex > 0);

/**
 * Hook to check if redo is available
 * @returns True if redo is available
 */
export const useCanRedo = () => useTemplateStore(state => state.historyIndex < state.history.length - 1);

/**
 * Hook to access all template slots
 * @returns Array of all slots in the template
 */
export const useTemplateSlots = () => useTemplateStore(state => state.template.slots || []);

/**
 * Hook to access slots filtered by entity type
 * @param entityType - Entity type to filter by
 * @returns Array of slots with the specified entity type
 */
export const useSlotsByEntityType = (entityType: EntityType) => 
  useTemplateStore(state => (state.template.slots || []).filter(slot => slot.entityType === entityType));

/**
 * Hook to access all slot actions
 * @returns Object containing all slot actions
 */
export const useSlotActions = () => useTemplateStore(state => ({
  addSlot: state.addSlot,
  updateSlot: state.updateSlot,
  deleteSlot: state.deleteSlot,
  getSlots: state.getSlots,
  getSlotsByEntityType: state.getSlotsByEntityType,
  isSlotInUse: state.isSlotInUse,
  getElementsUsingSlot: state.getElementsUsingSlot,
}));

/**
 * Hook to load a template from API with Context integration
 * @returns Function to load template by ID and loading state
 */
export const useLoadTemplateFromAPI = () => {
  const fetchTemplate = useFetchTemplate();
  const isLoading = useTemplateStore((state) => state.isLoading);

  const loadTemplate = useMemo(() => async (templateId: string) => {
    useTemplateStore.setState({ isLoading: true });
    try {
      const templateData = await fetchTemplate(templateId);
      
      // Validate template data before loading
      const validation = zTemplate.safeParse(templateData);
      if (validation.success) {
        const validatedTemplate = validation.data;
        useTemplateStore.setState({
          template: validatedTemplate,
          selectedElementId: null,
          history: [structuredClone(validatedTemplate)],
          historyIndex: 0,
        });
      } else {
        console.error('Failed to load and validate template:', validation.error);
        throw new Error('Template validation failed');
      }
    } catch (error) {
      console.error('Failed to load template from API:', error);
      throw error;
    } finally {
      useTemplateStore.setState({ isLoading: false });
    }
  }, [fetchTemplate]);

  return { loadTemplate, isLoading };
};

/**
 * Hook to save template to API with Context integration
 * @returns Function to save template and saving state
 */
export const useSaveTemplateToAPI = () => {
  const saveTemplateAPI = useSaveTemplate();
  const isSaving = useTemplateStore((state) => state.isSaving);
  const lastSaved = useTemplateStore((state) => state.lastSaved);
  const { generateThumbnail } = useTemplateExport();
  const { user } = useContext(Context);
  const plan = user?.subscriptionType || 'noplan';
  const tenant = user?.organizationId;

  const saveTemplate = useCallback(async (options?: { incrementVersion?: boolean }) => {
    useTemplateStore.setState({ isSaving: true });
    const startedAt = performance.now();
    let saveSucceeded = false;
    try {
      // Get the current template from store state at save time (not stale hook value)
      const currentTemplate = useTemplateStore.getState().template;

      return await withTelemetrySpan(
        'template.save',
        'feature.template.save',
        {
          'template.id': currentTemplate.id,
          'template.aspect_ratio': currentTemplate.aspectRatio,
          'template.element_count': currentTemplate.elements.length,
          'template.visible_element_count': currentTemplate.elements.filter((element) => element.visible !== false).length,
          'template.slot_count': currentTemplate.slots?.length ?? 0,
          'template.canvas.width': currentTemplate.canvas.width,
          'template.canvas.height': currentTemplate.canvas.height,
          'template.version_incremented': Boolean(options?.incrementVersion),
        },
        async () => {
          // Generate thumbnail
          let thumbnailData: string | undefined;
          try {
            thumbnailData = await generateThumbnail(currentTemplate);
          } catch (err) {
            console.error('Failed to generate thumbnail during save:', err);
          }

          annotateActiveSpan(
            {
              'template.thumbnail.generated': Boolean(thumbnailData),
            },
            {
              'template.element_count': { value: currentTemplate.elements.length, unit: 'none' },
              'template.slot_count': { value: currentTemplate.slots?.length ?? 0, unit: 'none' },
            },
          );

          const savedTemplate = await saveTemplateAPI({ ...currentTemplate, thumbnailData }, options);
          
          // Update the store with the saved template (includes updated metadata)
          // This won't trigger auto-save since we now listen to history changes, not template changes
          useTemplateStore.setState({ 
            template: savedTemplate,
            lastSaved: new Date() 
          });
          saveSucceeded = true;
          return savedTemplate;
        },
        {
          tags: {
            ...(tenant ? { tenant } : {}),
            plan,
          },
          metricAttributes: {
            plan,
          },
        },
      );
    } catch (error) {
      console.error('Failed to save template to API:', error);
      annotateActiveSpan({
        'template.save.success': false,
      });
      throw error;
    } finally {
      recordDistributionMetric('template.save.duration_ms', performance.now() - startedAt, 'millisecond', {
        plan,
        success: saveSucceeded,
      });
      useTemplateStore.setState({ isSaving: false });
    }
  }, [generateThumbnail, plan, saveTemplateAPI, tenant]);

  return { saveTemplate, isSaving, lastSaved };
};

/**
 * Hook to access all template actions (updated with slot actions)
 * @returns Object containing all template actions
 */
export const useTemplateActions = () => useTemplateStore(state => ({
  updateTemplateTitle: state.updateTemplateTitle,
  updateTemplateAspectRatio: state.updateTemplateAspectRatio,
  updateCanvas: state.updateCanvas,
  addElement: state.addElement,
  updateElement: state.updateElement,
  deleteElement: state.deleteElement,
  selectElement: state.selectElement,
  moveElementUp: state.moveElementUp,
  moveElementDown: state.moveElementDown,
  moveElementToTop: state.moveElementToTop,
  moveElementToBottom: state.moveElementToBottom,
  reorderElements: state.reorderElements,
  addSlot: state.addSlot,
  updateSlot: state.updateSlot,
  deleteSlot: state.deleteSlot,
  undo: state.undo,
  redo: state.redo,
  toggleElementVisibility: state.toggleElementVisibility,
  copyElement: state.copyElement,
  pasteElement: state.pasteElement,
  duplicateElement: state.duplicateElement,
  centerElement: state.centerElement,
}));

/**
 * Hook to get elements sorted by z-index (for LayersPanel)
 * Elements with higher z-index appear at the top of the list
 * @returns Array of elements sorted by z-index (descending)
 */
export const useSortedElements = () => {
  const elements = useTemplateElements();

  return useMemo(() => {
    return [...elements].sort((a, b) => {
       // Watermarks always first (top of list)
       if (a.isWatermark && !b.isWatermark) return -1;
       if (!a.isWatermark && b.isWatermark) return 1;
       
       return b.zIndex - a.zIndex;
    });
  }, [elements]);
};

/**
 * Hook to get visible elements sorted by z-index (for Canvas)
 * Elements with higher z-index are rendered later (appear on top)
 * @returns Array of visible elements sorted by z-index (ascending)
 */
export const useVisibleSortedElements = () => {
  const elements = useTemplateElements();

  return useMemo(() => {
    return elements
      .filter(el => el.visible)
      .sort((a, b) => {
        // Watermarks always go to the end (top)
        if (a.isWatermark && !b.isWatermark) return 1;
        if (!a.isWatermark && b.isWatermark) return -1;
        
        return a.zIndex - b.zIndex;
      });
  }, [elements]);
};
